import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowLeft, MapPin, Sparkle } from "@phosphor-icons/react";
import { travelDateApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { TierBadge } from "@/pages/TravelDate";
import { useTranslation, Trans } from "react-i18next";

const CORAL = "#F4A28F";

export default function TravelDateHistory() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [tab, setTab] = useState("planned");
    const [buckets, setBuckets] = useState({ planned: [], completed: [], favorites: [], shared: [] });

    const TABS = [
        { k: "planned",   l: t("travelDateHistory.tabs.planned") },
        { k: "completed", l: t("travelDateHistory.tabs.completed") },
        { k: "favorites", l: t("travelDateHistory.tabs.favorites") },
        { k: "shared",    l: t("travelDateHistory.tabs.shared") },
    ];

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/auth"); return; }
        travelDateApi.history().then(setBuckets).catch(() => {});
    }, [user, loading, navigate]);

    const list = buckets[tab] || [];
    const total = new Set(Object.values(buckets).flat().map(d => d.id)).size;

    return (
        <div className="min-h-screen grain" data-testid="td-history">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(244,162,143,0.15)" }}>
                <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/traveldate" className="flex items-center gap-3">
                        <ArrowLeft size={16} color={CORAL} />
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">TravelDate™</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: CORAL }}>{t("travelDateHistory.myTravelDates")}</div>
                        </div>
                    </Link>
                    <Link to="/traveldate" className="text-xs !py-2 !px-4 rounded-full font-semibold inline-flex items-center gap-1.5" style={{ background: CORAL, color: "var(--navy)" }}>
                        <Heart size={11} weight="fill" /> {t("travelDateHistory.newDate")}
                    </Link>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10">
                <div className="overline mb-2" style={{ color: CORAL }}>{t("travelDateHistory.overline")}</div>
                <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight tracking-tight">{t("travelDateHistory.title")}</h1>
                <p className="text-white/60 mt-3">
                    <Trans i18nKey="travelDateHistory.countText" count={total}>
                        You have <b style={{ color: CORAL }}>{{ count: total }}</b> saved dates.
                    </Trans>
                </p>

                <div className="mt-8 flex gap-2 flex-wrap">
                    {TABS.map((tabItem) => {
                        const count = (buckets[tabItem.k] || []).length;
                        return (
                            <button
                                key={tabItem.k}
                                onClick={() => setTab(tabItem.k)}
                                className="text-xs px-3.5 py-2 rounded-full inline-flex items-center gap-2 transition-all"
                                style={{
                                    background: tab === tabItem.k ? "rgba(244,162,143,0.18)" : "rgba(255,255,255,0.04)",
                                    border: `1px solid ${tab === tabItem.k ? CORAL + "66" : "rgba(255,255,255,0.08)"}`,
                                    color: tab === tabItem.k ? CORAL : "rgba(255,255,255,0.75)",
                                }}
                                data-testid={`td-hist-tab-${tabItem.k}`}
                            >
                                {tabItem.l} <span className="text-[10px] opacity-70">{count}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-8">
                    {list.length === 0 ? (
                        <div className="rounded-3xl p-10 text-center" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                            <Heart size={20} weight="fill" color={CORAL} className="mx-auto mb-3" />
                            <div className="text-white/70 mb-4">{t("travelDateHistory.noDates", { tab: TABS.find((x) => x.k === tab)?.l?.toLowerCase() || tab })}</div>
                            <Link to="/traveldate" className="text-xs !py-2 !px-4 rounded-full font-semibold inline-flex items-center gap-1.5" style={{ background: CORAL, color: "var(--navy)" }}>{t("travelDateHistory.findADate")}</Link>
                        </div>
                    ) : (
                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {list.map((d, i) => (
                                <Link key={d.id} to={`/traveldate/${d.id}`} className="group rounded-3xl overflow-hidden transition-transform hover:-translate-y-1" style={{ background: "var(--navy-deep)", border: "1px solid rgba(244,162,143,0.15)" }} data-testid={`td-hist-card-${i}`}>
                                    <div className="aspect-[16/10] bg-cover bg-center relative" style={{ backgroundImage: `url(${d.hero_image})` }}>
                                        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0) 45%, rgba(5,10,17,0.95) 100%)" }} />
                                        <div className="absolute top-3 left-3"><TierBadge tier={d.tier} /></div>
                                        <div className="absolute top-3 right-3 rounded-full px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase" style={{ background: "rgba(11,19,32,0.7)", color: CORAL, border: `1px solid ${CORAL}55`, backdropFilter: "blur(6px)" }}>
                                            {d.score}/100
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-4">
                                            <div className="font-serif text-xl text-white leading-tight line-clamp-2">❤️ {d.title}</div>
                                            <div className="text-white/70 text-[11px] mt-1 flex items-center gap-1">
                                                <MapPin size={10} weight="fill" color={CORAL} /> {d.city} · {d.duration_hours}h · ${d.estimated_cost_usd || "—"}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
